import { keepPreviousData, useMutation, useQueryClient } from '@tanstack/react-query';
import { growthKeys } from '@/data/queryKeys';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { useNotify } from '@/data/notifications';
import {
  COMPETITORS_PAGE_SIZE,
  addCompetitor,
  exportCompetitorCrawlPdf,
  fetchCompetitorCrawl,
  fetchCompetitorDossier,
  fetchCompetitors,
  rebuildCompetitors,
  recrawlCompetitor,
  removeCompetitor,
} from '../api';

export function useCompetitors(page = 1, limit = COMPETITORS_PAGE_SIZE) {
  return useGrowthQuery({
    queryKey: growthKeys.competitors.list({ page, limit }),
    queryFn: () => fetchCompetitors({ page, limit }),
    placeholderData: keepPreviousData,
  });
}

export function useCompetitorDossier(id: string, enabled = true) {
  return useGrowthQuery({
    queryKey: growthKeys.competitors.dossier(id),
    queryFn: () => fetchCompetitorDossier(id),
    enabled: enabled && Boolean(id),
  });
}

export function useCompetitorCrawl(id: string, enabled = true) {
  return useGrowthQuery({
    queryKey: growthKeys.competitors.crawl(id),
    queryFn: () => fetchCompetitorCrawl(id),
    enabled: enabled && Boolean(id),
  });
}

export function useCompetitorMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [...growthKeys.all, 'competitors'] });

  const add = useMutation({
    mutationFn: addCompetitor,
    onSuccess: () => {
      notify.success('Competitor added');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const remove = useMutation({
    mutationFn: removeCompetitor,
    onSuccess: () => {
      notify.success('Competitor removed');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const rebuild = useMutation({
    mutationFn: rebuildCompetitors,
    onSuccess: () => {
      notify.success('Competitor rebuild started');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const recrawl = useMutation({
    mutationFn: recrawlCompetitor,
    onSuccess: () => {
      notify.success('Recrawl started');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const exportCrawlPdf = useMutation({
    mutationFn: exportCompetitorCrawlPdf,
    onSuccess: () => notify.success('Crawl report exported'),
    onError: (e) => notify.error(e),
  });

  return { add, remove, rebuild, recrawl, exportCrawlPdf };
}
